import './input-honorific-block-minutes-controller.scss';

import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Honorific } from '../../../../data/types/api/graphql';
import { InputCheckbox } from '../../../../elements/form/input/input-checkbox';
import { InputSelect } from '../../../../elements/form/input/input-select';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Tooltip } from '../../../../elements/tooltip/tooltip';

export type BlockMinuteItem = {
  key: Honorific;
  title: string | JSX.Element;
  defaultValue: number;
  values: {
    key: string;
    title: string;
    available: boolean;
    irregular: boolean;
  }[];
};

type InputHonorificBlockMinutesControllerProps = {
  label?: JSX.Element | string;
  name: string;
  showIrregular?: boolean;
  options: BlockMinuteItem[];
  onChange?: (value: { key: Honorific; value: number }[]) => void;
  value?: { key: Honorific; value: number }[];
  defaultValue?: { key: Honorific; value: number }[];
};

export function InputHonorificBlockMinutesController({
  label,
  name,
  showIrregular = false,
  options,
  onChange,
  value: propValue,
  defaultValue,
}: InputHonorificBlockMinutesControllerProps) {
  const { setValue } = useFormContext();

  // State for selected honorifics
  const [checkedItems, setCheckedItems] = useState<Map<Honorific, boolean>>(
    () => {
      const initialChecked = new Map();
      (propValue ?? defaultValue ?? []).forEach(({ key }) => {
        initialChecked.set(key, true);
      });
      return initialChecked;
    }
  );

  // State for select values
  const [items, setItems] = useState<Map<Honorific, string>>(() => {
    const initialValues = propValue ?? defaultValue ?? [];
    return new Map(
      initialValues.map(({ key, value }) => [key, value.toString()])
    );
  });

  // Sync propValue changes
  useEffect(() => {
    if (!propValue) return;

    const newChecked = new Map();
    const newItems = new Map();

    propValue.forEach(({ key, value }) => {
      newChecked.set(key, true);
      newItems.set(key, value.toString());
    });

    setCheckedItems(newChecked);
    setItems(newItems);
  }, [propValue]);

  // Sync state changes to react-hook-form
  useEffect(() => {
    const values = Array.from(items.entries()).map(([key, value]) => ({
      key,
      value: parseInt(value),
    }));

    setValue(name, values, {
      shouldDirty: true,
      shouldValidate: true,
    });

    onChange?.(values);
  }, [items, name, onChange, setValue]);

  // Handle checkbox change
  const handleSelect = (honorific: Honorific, val: boolean | boolean[]) => {
    const isSelected = Array.isArray(val)
      ? val.some(x => x === true)
      : val === true;

    setCheckedItems(prev => {
      const newCheckedItems = new Map(prev);
      newCheckedItems.set(honorific, isSelected);
      return newCheckedItems;
    });

    setItems(prev => {
      const newItems = new Map(prev);
      const defaultOption =
        options.find(opt => opt.key === honorific)?.defaultValue.toString() ??
        '0';

      if (isSelected) {
        newItems.set(honorific, defaultOption);
      } else {
        newItems.delete(honorific);
      }

      return newItems;
    });
  };

  // Handle select box value change
  const handleChange = (honorific: Honorific, val: string) => {
    setItems(prev => {
      const newItems = new Map(prev);
      if (val) {
        newItems.set(honorific, val);
      } else {
        newItems.delete(honorific);
      }
      return newItems;
    });
  };

  return (
    <Controller
      name={name}
      render={({ fieldState: { error } }) => (
        <div className="input-honorific-block-minutes">
          {label && <label htmlFor={name}>{label}</label>}
          <div className="honorific-block-minutes-items">
            {options.map(option => (
              <div key={option.key} className="honorific-block-minutes-item">
                <InputCheckbox<boolean>
                  name={`${name}.select`}
                  options={[{ key: true }]}
                  onChange={val => handleSelect(option.key, val)}
                  value={checkedItems.get(option.key) ?? false}
                />
                <div className="honorific-title">{option.title}</div>

                {checkedItems.get(option.key) && (
                  <>
                    {items.get(option.key) &&
                      option.values.find(v => v.key === items.get(option.key))
                        ?.available === false && (
                        <Tooltip
                          message={
                            <CustomFormattedMessage id="common.honorific-block-minutes-not-available" />
                          }
                        >
                          <i className="icon info" />
                        </Tooltip>
                      )}
                    <InputSelect<string>
                      name={`${name}.${option.key}`}
                      value={items.get(option.key) ?? '0'}
                      options={
                        showIrregular
                          ? option.values
                          : option.values.filter(x => x.irregular === false)
                      }
                      onChange={val => handleChange(option.key, val)}
                    />
                  </>
                )}
              </div>
            ))}
          </div>

          {error && <div className="input-error">{error.message}</div>}
        </div>
      )}
    />
  );
}
